/**
 * Activity log for emoji reactions on Project Hub / Architect Discussion comments.
 * Written client-side on add (not remove, not self-react). No Slack / email / bell.
 */

import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import { getProjectDisplayName } from './projectName';
import { buildHiringCandidatePath } from './hiringResumeViewer';
import {
  buildArchitectPortalDiscussionPath,
  buildClientWorkspacePath,
  buildPdfMentionProjectHubPath,
  buildProjectHubGroupCommentPath,
} from './notificationDeepLinks';
import type { EnhancedDeal } from '../types';

export const COMMENT_REACTION_EVENTS_COLLECTION = 'commentReactionEvents';
export const COMMENT_REACTION_FEED_LIMIT = 10;
export const COMMENT_REACTION_PREVIEW_MAX = 60;

export type CommentReactionEvent = {
  id: string;
  commentAuthorId: string;
  actorId: string;
  emoji: string;
  commentId: string;
  commentPreview: string;
  projectId: string | null;
  groupCommentKey: string | null;
  dealId: string | null;
  audience: 'internal' | 'architect' | null;
  projectLabel: string;
  documentId: string | null;
  pdfPageNumber: number | null;
  /** Team note on a lead (`leads/{leadId}/comments`). */
  leadId?: string | null;
  /** Ops Hub task (`opsComments.taskId`). */
  opsTaskId?: string | null;
  /** Hiring candidate (`jobApplicationComments.applicationId`). */
  applicationId?: string | null;
  /** Marketing content request (`marketingRequestComments.requestId`). */
  requestId?: string | null;
  createdAt: Date;
};

/** Hub comments use `authorId`; leads / ops / PDF pins use `userId`. */
export function commentAuthorIdFromData(
  data: Record<string, unknown> | null | undefined,
  fallback = '',
): string {
  if (!data) return String(fallback || '').trim();
  return String(data.authorId || data.userId || data.createdBy || fallback || '').trim();
}

export function commentPreviewFromReactionData(data: Record<string, unknown> | null | undefined): string {
  if (!data) return '';
  const body = typeof data.body === 'string' ? data.body : typeof data.text === 'string' ? data.text : typeof data.content === 'string' ? data.content : '';
  const bodyHtml = typeof data.bodyHtml === 'string' ? data.bodyHtml : '';
  return commentPreviewFromFields(body, bodyHtml);
}

export function shouldRecordCommentReactionEvent(args: {
  added: boolean;
  actorId: string;
  authorId: string;
}): boolean {
  const actorId = args.actorId.trim();
  const authorId = args.authorId.trim();
  return args.added && !!actorId && !!authorId && actorId !== authorId;
}

export function commentPreviewFromFields(
  body?: string | null,
  bodyHtml?: string | null,
  max = COMMENT_REACTION_PREVIEW_MAX,
): string {
  const fromBody = String(body || '').replace(/\s+/g, ' ').trim();
  const fromHtml = String(bodyHtml || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
  const plain = fromBody || fromHtml;
  if (plain.length <= max) return plain;
  const keep = Math.max(0, max - 1);
  return `${plain.slice(0, keep)}…`;
}

export function resolveReactionProjectLabel(args: {
  storedLabel?: string | null;
  projectId?: string | null;
  dealId?: string | null;
  deals?: Array<Pick<EnhancedDeal, 'id' | 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'>>;
  projects?: Array<{ id?: string; dealId?: string; name?: string }>;
}): string {
  const projectId = (args.projectId || '').trim();
  const project = args.projects?.find((p) => p.id === projectId);
  const dealId = (args.dealId || '').trim() || (project?.dealId || '').trim();
  const deal = dealId ? args.deals?.find((d) => d.id === dealId) : undefined;
  const live = getProjectDisplayName(deal);
  if (live) return live;
  const name = (project?.name || '').trim();
  if (name) return name;
  return (args.storedLabel || '').trim();
}

export function buildCommentReactionEventPath(
  event: Pick<CommentReactionEvent, 'projectId' | 'groupCommentKey' | 'commentId' | 'audience'> &
    Partial<Pick<CommentReactionEvent, 'documentId' | 'pdfPageNumber' | 'leadId' | 'opsTaskId' | 'applicationId' | 'requestId' | 'dealId'>>,
  viewerRole?: string | null,
): string | null {
  const projectId = (event.projectId || '').trim();
  const commentId = (event.commentId || '').trim() || undefined;
  const documentId = (event.documentId || '').trim();
  if (projectId && documentId && commentId) {
    return buildPdfMentionProjectHubPath({
      projectId,
      documentId,
      annotationId: commentId,
      pdfPageNumber: event.pdfPageNumber ?? undefined,
    });
  }
  if (projectId) {
    if (String(viewerRole || '').trim().toLowerCase() === 'architect') {
      return buildArchitectPortalDiscussionPath({ projectId, commentId });
    }
    const groupCommentKey = (event.groupCommentKey || '').trim();
    if (groupCommentKey) {
      return buildProjectHubGroupCommentPath({
        projectId,
        groupCommentKey,
        commentId,
        discussion: event.audience === 'architect' ? 'architect' : 'comments',
      });
    }
    const q = new URLSearchParams();
    if (commentId) q.set('taskComment', commentId);
    const qs = q.toString();
    return qs ? `/project-hub/${projectId}?${qs}` : `/project-hub/${projectId}`;
  }
  const leadId = (event.leadId || '').trim();
  if (leadId) {
    return buildClientWorkspacePath(leadId, {
      dealId: event.dealId || undefined,
      comms: 'comments',
      commentId,
    });
  }
  const opsTaskId = (event.opsTaskId || '').trim();
  if (opsTaskId) {
    const q = new URLSearchParams();
    q.set('task', opsTaskId);
    if (commentId) q.set('comment', commentId);
    return `/operations?${q.toString()}`;
  }
  const applicationId = (event.applicationId || '').trim();
  if (applicationId) {
    return buildHiringCandidatePath({ applicationId, teamCommentId: commentId });
  }
  const requestId = (event.requestId || '').trim();
  if (requestId) {
    const q = new URLSearchParams();
    q.set('request', requestId);
    if (commentId) q.set('comment', commentId);
    return `/marketing-hub/content-requests?${q.toString()}`;
  }
  return null;
}

function stringField(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Where a stored comment document should open, from its collection path. */
export function commentReactionPathFromStoredComment(args: {
  commentId: string;
  collectionPath: string;
  data: Record<string, unknown>;
  viewerRole?: string | null;
}): string | null {
  const parts = args.collectionPath.split('/').filter(Boolean);
  const root = parts[0] || '';
  const leadFromPath =
    (root === 'leads' || root === 'testLeads') && parts[2] === 'comments' ? parts[1] : null;
  const audience =
    args.data.audience === 'architect' ? 'architect' : args.data.audience === 'internal' ? 'internal' : null;
  const pageRaw = args.data.pageNumber ?? args.data.pdfPageNumber;
  return buildCommentReactionEventPath(
    {
      projectId: stringField(args.data, 'projectId'),
      groupCommentKey: stringField(args.data, 'groupCommentKey'),
      commentId: args.commentId,
      audience,
      documentId: stringField(args.data, 'documentId'),
      pdfPageNumber: typeof pageRaw === 'number' && Number.isFinite(pageRaw) ? pageRaw : null,
      leadId: leadFromPath || stringField(args.data, 'leadId'),
      dealId: stringField(args.data, 'dealId'),
      opsTaskId: root === 'opsComments' ? stringField(args.data, 'taskId') : null,
      applicationId: root === 'jobApplicationComments' ? stringField(args.data, 'applicationId') : null,
      requestId: root === 'marketingRequestComments' ? stringField(args.data, 'requestId') : null,
    },
    args.viewerRole,
  );
}

const REACTION_COMMENT_COLLECTIONS = [
  'taskComments',
  'opsComments',
  'marketingRequestComments',
  'jobApplicationComments',
  'pdfAnnotations',
] as const;

/**
 * Older reaction rows were saved without a project, so the list disabled them.
 * The comment document still knows whether it is a team note, ops task, or hiring thread.
 */
export async function lookupCommentReactionDestination(
  db: Firestore,
  event: Pick<CommentReactionEvent, 'commentId'>,
  viewerRole?: string | null,
): Promise<string | null> {
  const commentId = (event.commentId || '').trim();
  if (!commentId) return null;
  for (const collectionName of REACTION_COMMENT_COLLECTIONS) {
    try {
      const snap = await getDoc(doc(db, collectionName, commentId));
      if (!snap.exists()) continue;
      const path = commentReactionPathFromStoredComment({
        commentId,
        collectionPath: snap.ref.path,
        data: snap.data() as Record<string, unknown>,
        viewerRole,
      });
      if (path) return path;
    } catch (error) {
      console.error(`[commentReaction] lookup ${collectionName}/${commentId} failed`, error);
    }
  }
  try {
    const snap = await getDocs(
      query(collectionGroup(db, 'comments'), orderBy('createdAt', 'desc'), limit(400)),
    );
    const hit = snap.docs.find((row) => row.id === commentId);
    if (!hit) return null;
    return commentReactionPathFromStoredComment({
      commentId,
      collectionPath: hit.ref.path,
      data: hit.data() as Record<string, unknown>,
      viewerRole,
    });
  } catch (error) {
    console.error('[commentReaction] team-note lookup failed', error);
    return null;
  }
}

function compact(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export async function recordTaskCommentReactionEvent(
  db: Firestore,
  args: {
    actorId: string;
    emoji: string;
    commentId: string;
    authorId: string;
    comment: {
      body?: string | null;
      bodyHtml?: string | null;
      text?: string | null;
      content?: string | null;
      projectId?: string | null;
      groupCommentKey?: string | null;
      dealId?: string | null;
      audience?: string | null;
      documentId?: string | null;
      pageNumber?: number | null;
      leadId?: string | null;
      taskId?: string | null;
      applicationId?: string | null;
      requestId?: string | null;
    };
    /** Firestore path of the comment, so team notes and ops threads keep a destination. */
    commentPath?: string | null;
    projectLabel?: string | null;
  },
): Promise<void> {
  if (
    !shouldRecordCommentReactionEvent({
      added: true,
      actorId: args.actorId,
      authorId: args.authorId,
    })
  ) {
    return;
  }
  const emoji = args.emoji.trim();
  const commentId = args.commentId.trim();
  if (!emoji || !commentId) return;
  const audience = args.comment.audience === 'architect' ? 'architect' : args.comment.audience === 'internal' ? 'internal' : null;
  const pageRaw = args.comment.pageNumber;
  const pdfPageNumber =
    typeof pageRaw === 'number' && Number.isFinite(pageRaw) ? pageRaw : null;
  const pathParts = String(args.commentPath || '').split('/').filter(Boolean);
  const root = pathParts[0] || '';
  const leadFromPath =
    (root === 'leads' || root === 'testLeads') && pathParts[2] === 'comments' ? pathParts[1] : '';
  await addDoc(
    collection(db, COMMENT_REACTION_EVENTS_COLLECTION),
    compact({
      commentAuthorId: args.authorId.trim(),
      actorId: args.actorId.trim(),
      emoji,
      commentId,
      commentPreview: commentPreviewFromFields(
        args.comment.body || args.comment.text || args.comment.content,
        args.comment.bodyHtml,
      ),
      projectId: (args.comment.projectId || '').trim() || null,
      groupCommentKey: (args.comment.groupCommentKey || '').trim() || null,
      dealId: (args.comment.dealId || '').trim() || null,
      audience,
      projectLabel: (args.projectLabel || '').trim() || null,
      documentId: (args.comment.documentId || '').trim() || null,
      pdfPageNumber,
      leadId: leadFromPath || (args.comment.leadId || '').trim() || null,
      opsTaskId: root === 'opsComments' ? (args.comment.taskId || '').trim() || null : null,
      applicationId: root === 'jobApplicationComments' ? (args.comment.applicationId || '').trim() || null : null,
      requestId: root === 'marketingRequestComments' ? (args.comment.requestId || '').trim() || null : null,
      createdAt: Timestamp.now(),
    }),
  );
}

export function parseCommentReactionEvent(
  id: string,
  data: Record<string, unknown>,
): CommentReactionEvent {
  const createdAtRaw = data.createdAt as { toDate?: () => Date } | Date | undefined;
  const createdAt =
    createdAtRaw instanceof Date
      ? createdAtRaw
      : createdAtRaw?.toDate?.() instanceof Date
        ? createdAtRaw.toDate()
        : new Date();
  const audience = data.audience === 'architect' ? 'architect' : data.audience === 'internal' ? 'internal' : null;
  return {
    id,
    commentAuthorId: String(data.commentAuthorId || '').trim(),
    actorId: String(data.actorId || '').trim(),
    emoji: String(data.emoji || '').trim(),
    commentId: String(data.commentId || '').trim(),
    commentPreview: String(data.commentPreview || '').trim(),
    projectId: String(data.projectId || '').trim() || null,
    groupCommentKey: String(data.groupCommentKey || '').trim() || null,
    dealId: String(data.dealId || '').trim() || null,
    audience,
    projectLabel: String(data.projectLabel || '').trim(),
    documentId: String(data.documentId || '').trim() || null,
    pdfPageNumber:
      typeof data.pdfPageNumber === 'number' && Number.isFinite(data.pdfPageNumber)
        ? data.pdfPageNumber
        : null,
    leadId: String(data.leadId || '').trim() || null,
    opsTaskId: String(data.opsTaskId || '').trim() || null,
    applicationId: String(data.applicationId || '').trim() || null,
    requestId: String(data.requestId || '').trim() || null,
    createdAt,
  };
}

export function sortCommentReactionEventsNewestFirst(
  events: CommentReactionEvent[],
): CommentReactionEvent[] {
  return [...events].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function subscribeCommentReactionEvents(
  db: Firestore,
  authorId: string,
  onNext: (events: CommentReactionEvent[]) => void,
): Unsubscribe {
  const uid = authorId.trim();
  if (!uid) {
    onNext([]);
    return () => undefined;
  }
  const col = collection(db, COMMENT_REACTION_EVENTS_COLLECTION);
  const emit = (docs: Array<{ id: string; data: () => Record<string, unknown> }>) => {
    const rows = sortCommentReactionEventsNewestFirst(
      docs.map((d) => parseCommentReactionEvent(d.id, d.data())),
    ).slice(0, COMMENT_REACTION_FEED_LIMIT);
    onNext(rows);
  };
  const ordered = query(
    col,
    where('commentAuthorId', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(COMMENT_REACTION_FEED_LIMIT),
  );
  let unsubFallback: Unsubscribe | undefined;
  const unsubPrimary = onSnapshot(
    ordered,
    (snap) => {
      onNext(snap.docs.map((d) => parseCommentReactionEvent(d.id, d.data() as Record<string, unknown>)));
    },
    (error) => {
      console.error('[commentReactionEvents] listen failed; trying unordered fallback', error);
      const fallback = query(col, where('commentAuthorId', '==', uid), limit(40));
      unsubFallback = onSnapshot(
        fallback,
        (snap) => emit(snap.docs),
        (fallbackError) => {
          console.error('[commentReactionEvents] fallback listen failed', fallbackError);
          onNext([]);
        },
      );
    },
  );
  return () => {
    unsubPrimary();
    unsubFallback?.();
  };
}
